import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Editor, { TABLE_CELL_COMPONENT, FORM_FIELD_COMPONENT } from '../Editor';
import './TextFieldEditor.scss';

class TableCellEditor extends React.PureComponent {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.string), // For multi-lines
    ]),
    object: PropTypes.shape({}),
    editing: PropTypes.bool,
    label: PropTypes.string,
    type: PropTypes.string,
    error: PropTypes.string,
    readOnly: PropTypes.bool,
    property: PropTypes.shape({
      formatValue: PropTypes.func,
      name: PropTypes.string,
      type: PropTypes.string,
      margin: PropTypes.string,
      multiline: PropTypes.bool,
      description: PropTypes.string,
      debounce: PropTypes.number,
    }),
    form: PropTypes.bool,
  }

  static defaultProps = {
    error: null,
    value: undefined,
    object: undefined,
    readOnly: false,
    label: null,
    editing: false,
    type: 'string',
    property: undefined,
    form: false,
  };

  static getDerivedStateFromProps(props, state = {}) {
    if (state.origValue !== props.value) {
      return { value: props.value, origValue: props.value };
    }
    return null;
  }

  constructor(props) {
    super(props);

    // eslint getting confused: origValue is used in getDerivedStateFromProps
    // eslint-disable-next-line react/no-unused-state
    this.state = { value: props.value, origValue: props.value };
    this.handleChange = this.handleChange.bind(this);
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  handleChange = (event) => {
    const { onChange, type, property } = this.props;
    if (onChange) {
      let { value } = event.target;
      if (type === 'integer') {
        value = parseInt(value, 10);
      } else if (type === 'number') {
        value = parseFloat(value);
      }
      const { debounce } = property;
      if (debounce) {
        this.setState({ value }, () => {
          if (this.timeout) {
            clearTimeout(this.timeout);
          }
          this.timeout = setTimeout(() => {
            this.timeout = null;
            if (!this.unmounted) {
              onChange(value);
            }
          }, debounce);
        });
      } else {
        onChange(value);
      }
    }
  };

  render() {
    const {
      editing,
      error,
      label,
      readOnly,
      type,
      property,
      object,
      form,
    } = this.props;
    const { value } = this.state;
    const adornments = {};
    const fvalue = object && property && property.formatValue
          ? property.formatValue(value, object, property.name, { ...property, adornments })
          : value;

    const InputProps = {
      readOnly: Boolean(readOnly === undefined ? true : readOnly),
      startAdornment: adornments.prefix ? <InputAdornment position="start">{adornments.prefix}</InputAdornment> : undefined,
      endAdornment: adornments.suffix ? <InputAdornment position="end">{adornments.suffix}</InputAdornment> : undefined,
      inputProps: (property.type === 'integer' || property.type === 'number')
            ? { style: { textAlign: 'right' } } : undefined,
      fullWidth: false,
    };
    const numeric = (!property.formatValue || (typeof fvalue !== 'string')) && (type === 'number' || type === 'integer');
    const { min, max, autoComplete } = property || {};
    if (((min !== undefined) || (max !== undefined)) && numeric) {
      InputProps.inputProps = Object.assign({}, InputProps.inputProps, { min, max });
    }
    if (autoComplete) {
      InputProps.inputProps = Object.assign({}, InputProps.inputProps, { autoComplete });
    }
    const margin = property.margin || (form ? 'normal' : 'dense');
    return (
      <div className={classnames('tuc-textfield-editor', InputProps.readOnly && 'grbrsm-readonly')}>
        {editing && (
          <TextField
            error={!!error}
            label={label}
            value={value === undefined ? ' ' : value}
            type={(value !== undefined) && numeric ? 'number' : undefined}
            margin={margin}
            onChange={this.handleChange}
            InputProps={InputProps}
            fullWidth={form}
            multiline={property.multiline || false}
            helperText={error || (form && property ? property.description : undefined)}
          />
        )}
        {!editing ? fvalue : ''}
      </div>
    );
  }
}

export default class TextFieldEditor extends Editor {
  constructor(name, components) {
    super(name, Object.assign({}, {
      [TABLE_CELL_COMPONENT]: [TableCellEditor],
      [FORM_FIELD_COMPONENT]: [TableCellEditor],
    }, components));
  }

  getSortValue(value) {
    return !value || !value.toLowerCase ? value : value.toLowerCase();
  }

  getFilterValue(value) {
    return value && value.toString() || (value === 0 ? '0' : '');
  }
}
